/**
 * C2 — "Collaborators". People (not organisations) who can see one of the
 * caller's projects, and at what level.
 *
 * They belong on this screen for the same reason the grid does: the question is
 * "who can see what of mine", and a person with edit access to a project is an
 * answer to it. They are listed separately from organisations because the two
 * are different kinds of thing — a collaborator is someone you invited to work
 * with you, an organisation is a portfolio you granted a view to — and merging
 * them would blur exactly the distinction this screen exists to keep sharp.
 *
 * WHAT IS SHOWN IS A NAME, and round 3 is why that sentence is in this file.
 * The card printed "Test · c91e9448-e081-7052-49f4-e92407dbb91e" on the live
 * screen — the person's raw Cognito sub — because the permissions row carried
 * neither a display name nor an email. A sub tells the reader nothing about who
 * can see their work, which is the only question this card answers.
 *
 * The order is: the display name the route resolves (it now fills one in from
 * the directory, name only), then the LOCAL PART of the email — "ben.tran", a
 * thing a person recognises, without publishing the full login on the screen —
 * and when there is neither, the honest "Unnamed collaborator". Never the sub,
 * never the whole email address.
 */
import { Link } from 'react-router-dom'

export interface CollaboratorRow {
  projectId: string
  projectName: string
  granteeId: string
  /** The person, already resolved to a name or an honest stand-in. Never an id. */
  grantee: string
  /** view · comment · edit · manager · admin */
  level: string
}

/** What nobody can be called on this card. */
export const UNNAMED_COLLABORATOR = 'Unnamed collaborator'

/**
 * The person's name for the screen: display name, else the email's local part,
 * else an honest stand-in. Exported so the page and the test agree on the rule.
 */
export function collaboratorName(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string {
  const name = (displayName ?? '').trim()
  if (name) return name
  const local = (email ?? '').trim().split('@')[0].trim()
  if (local) return local
  return UNNAMED_COLLABORATOR
}

export interface CollaboratorsPanelProps {
  collaborators: CollaboratorRow[]
  loaded: boolean
  error?: boolean
}

const LEVEL_TONE: Record<string, string> = {
  view: 'bg-slate-100 text-slate-600',
  comment: 'bg-iflow-100 text-iflow-700',
  edit: 'bg-amber-100 text-amber-800',
  manager: 'bg-amber-100 text-amber-800',
  admin: 'bg-rose-100 text-rose-700',
}

function levelLabel(level: string): string {
  if (!level) return 'Access'
  return level.charAt(0).toUpperCase() + level.slice(1)
}

export default function CollaboratorsPanel({ collaborators, loaded, error }: CollaboratorsPanelProps) {
  return (
    <div data-part="CollaboratorsPanel" className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <h2 className="mb-1 flex items-center gap-2 text-[14px] font-bold text-slate-900">
        Collaborators
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
          {loaded && !error ? collaborators.length : '—'}
        </span>
      </h2>

      {!loaded && !error && <p className="text-[12.5px] text-slate-400">Loading…</p>}
      {error && (
        <p className="text-[12.5px] text-rose-600">
          Could not load your collaborators — this list is not a statement about
          your sharing until it loads.
        </p>
      )}
      {loaded && !error && collaborators.length === 0 && (
        <p className="text-[12.5px] text-slate-600">
          Nobody else can see any of your projects.
        </p>
      )}

      {loaded && !error && collaborators.map((c) => (
        <div
          key={`${c.projectId}:${c.granteeId}`}
          className="flex items-center gap-2.5 border-t border-slate-100 py-2 text-[12.5px] first:border-t-0"
        >
          <span className="min-w-0 flex-1 truncate text-slate-600">
            <b className="text-slate-900">{c.projectName}</b> · {c.grantee}
          </span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${LEVEL_TONE[c.level] ?? LEVEL_TONE.view}`}>
            {levelLabel(c.level)}
          </span>
          <Link
            to={`/projects/${c.projectId}?tab=access`}
            className="shrink-0 text-[12px] font-semibold text-iflow-600 hover:text-iflow-800"
          >
            Change
          </Link>
        </div>
      ))}
    </div>
  )
}
